import { z } from 'zod';

const optionalNumber = z.union([z.number(), z.nan(), z.literal('')]).optional().nullable()
  .transform((v) => (v === '' || v == null || Number.isNaN(v) ? undefined : Number(v)));

const requiredPositive = (msg) =>
  z.coerce.number({ error: msg }).refine((n) => n > 0, { message: msg });

export const personalInfoSchema = z.object({
  personalInfo: z.object({
    firstName: z.string().trim().min(1, 'First name is required'),
    middleName: z.string().optional().nullable(),
    lastName: z.string().trim().min(1, 'Last name is required'),
    suffix: z.string().optional().nullable(),
    email: z.string().trim().email('Valid email is required'),
    phone: z.string().trim().min(1, 'Phone is required'),
    dateOfBirth: z.any().optional().nullable(),
    ssn: z.string().optional().nullable(),
    citizenship: z.string().optional().nullable(),
    maritalStatus: z.string().optional().nullable(),
    dependentsCount: optionalNumber,
    dependentsAges: z.string().optional().nullable(),
    currentAddress: z.string().trim().min(1, 'Address is required'),
    currentCity: z.string().trim().min(1, 'City is required'),
    currentState: z.string().trim().min(1, 'State is required'),
    currentZip: z.string().trim().min(1, 'ZIP is required'),
    yearsAtAddress: optionalNumber,
    housingStatus: z.string().optional().nullable(),
    monthlyRent: optionalNumber,
    previousAddress: z.string().optional().nullable(),
    previousCity: z.string().optional().nullable(),
    previousState: z.string().optional().nullable(),
    previousZip: z.string().optional().nullable(),
    yearsAtPrevAddress: optionalNumber,
  }),
});

export const employmentSchema = z.object({
  employment: z.object({
    employerName: z.string().trim().min(1, 'Employer name is required'),
    employerPhone: z.string().optional().nullable(),
    employerAddress: z.string().optional().nullable(),
    employerCity: z.string().optional().nullable(),
    employerState: z.string().optional().nullable(),
    employerZip: z.string().optional().nullable(),
    position: z.string().trim().min(1, 'Position is required'),
    employmentStartDate: z.any().optional().nullable(),
    yearsEmployed: optionalNumber,
    selfEmployed: z.boolean().optional().nullable(),
    baseIncome: requiredPositive('Base income is required'),
    overtime: optionalNumber,
    bonus: optionalNumber,
    commission: optionalNumber,
    otherIncome: optionalNumber,
    totalMonthlyIncome: optionalNumber,
    previousEmployerName: z.string().optional().nullable(),
    previousPosition: z.string().optional().nullable(),
    previousStartDate: z.any().optional().nullable(),
    previousEndDate: z.any().optional().nullable(),
    previousMonthlyIncome: optionalNumber,
  }),
});

export const assetsSchema = z.object({
  assets: z.object({
    checkingAccounts: optionalNumber,
    savingsAccounts: optionalNumber,
    retirementAccounts: optionalNumber,
    otherAssets: optionalNumber,
    giftFunds: optionalNumber,
    giftSource: z.string().optional().nullable(),
    totalAssets: z.coerce.number({ error: 'Total assets is required' })
      .refine((n) => n != null && !Number.isNaN(n), { message: 'Total assets is required' }),
  }).superRefine((data, ctx) => {
    if ((data.giftFunds || 0) > 0 && !data.giftSource?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Gift source is required when gift funds are entered',
        path: ['giftSource'],
      });
    }
  }),
});

export const liabilitiesSchema = z.object({
  liabilities: z.object({
    creditCardDebt: optionalNumber,
    carLoans: optionalNumber,
    studentLoans: optionalNumber,
    otherDebts: optionalNumber,
    alimonyChildSupport: optionalNumber,
    totalMonthlyDebt: z.coerce.number({ error: 'Monthly debt is required' })
      .refine((n) => n != null && !Number.isNaN(n), { message: 'Monthly debt is required' }),
  }),
});

export const propertySchema = z.object({
  property: z.object({
    address: z.string().trim().min(1, 'Property address is required'),
    city: z.string().trim().min(1, 'City is required'),
    state: z.string().trim().min(1, 'State is required'),
    zip: z.string().trim().min(1, 'ZIP is required'),
    county: z.string().optional().nullable(),
    propertyValue: requiredPositive('Property value is required'),
    propertyType: z.string().optional().nullable(),
    occupancyType: z.string().optional().nullable(),
    unitsCount: optionalNumber,
    mixedUse: z.boolean().optional().nullable(),
    manufacturedHome: z.boolean().optional().nullable(),
  }),
});

export const loanDetailsSchema = z.object({
  loanDetails: z.object({
    loanPurpose: z.string().trim().min(1, 'Loan purpose is required'),
    loanAmount: requiredPositive('Loan amount is required'),
    downPayment: optionalNumber,
    loanType: z.string().optional().nullable(),
    interestRate: optionalNumber,
    loanTerm: optionalNumber,
    currentMortgageBalance: optionalNumber,
    currentInterestRate: optionalNumber,
    cashOutAmount: optionalNumber,
    refinancePurpose: z.string().optional().nullable(),
  }).superRefine((data, ctx) => {
    const isRefi = data.loanPurpose === 'Refinance' || data.loanPurpose === 'Cash-Out Refinance';
    if (isRefi && !(data.currentMortgageBalance > 0)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Current mortgage balance is required',
        path: ['currentMortgageBalance'],
      });
    }
  }),
});

export const declarationsSchema = z.object({
  declarations: z.object({
    outstandingJudgments: z.boolean().optional().nullable(),
    bankruptcyLast7Years: z.boolean().optional().nullable(),
    foreclosureLast7Years: z.boolean().optional().nullable(),
    lawsuitParty: z.boolean().optional().nullable(),
    delinquentFederalDebt: z.boolean().optional().nullable(),
    alimonyObligation: z.boolean().optional().nullable(),
    downPaymentBorrowed: z.boolean().optional().nullable(),
    usCitizen: z.boolean().optional().nullable(),
    permanentResident: z.boolean().optional().nullable(),
    currentlyServing: z.boolean().optional().nullable(),
    previouslyServed: z.boolean().optional().nullable(),
  }),
});

export const STEP_SCHEMAS = [
  personalInfoSchema,
  employmentSchema,
  assetsSchema,
  liabilitiesSchema,
  propertySchema,
  loanDetailsSchema,
  declarationsSchema,
];

export const STEP_ROOT_KEYS = [
  'personalInfo',
  'employment',
  'assets',
  'liabilities',
  'property',
  'loanDetails',
  'declarations',
];

export const STEP_LABELS = [
  'Personal Info',
  'Employment & Income',
  'Assets',
  'Liabilities',
  'Property',
  'Loan Details',
  'Review & Declarations',
];

export const DEFAULT_APPLICATION_VALUES = {
  personalInfo: {
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    email: '',
    phone: '',
    dateOfBirth: null,
    ssn: '',
    citizenship: '',
    maritalStatus: '',
    dependentsCount: undefined,
    dependentsAges: '',
    currentAddress: '',
    currentCity: '',
    currentState: '',
    currentZip: '',
    yearsAtAddress: undefined,
    housingStatus: '',
    monthlyRent: undefined,
    previousAddress: '',
    previousCity: '',
    previousState: '',
    previousZip: '',
    yearsAtPrevAddress: undefined,
  },
  employment: {
    employerName: '',
    employerPhone: '',
    employerAddress: '',
    employerCity: '',
    employerState: '',
    employerZip: '',
    position: '',
    employmentStartDate: null,
    yearsEmployed: undefined,
    selfEmployed: false,
    baseIncome: undefined,
    overtime: undefined,
    bonus: undefined,
    commission: undefined,
    otherIncome: undefined,
    totalMonthlyIncome: undefined,
    previousEmployerName: '',
    previousPosition: '',
    previousStartDate: null,
    previousEndDate: null,
    previousMonthlyIncome: undefined,
  },
  assets: {
    checkingAccounts: undefined,
    savingsAccounts: undefined,
    retirementAccounts: undefined,
    otherAssets: undefined,
    giftFunds: undefined,
    giftSource: '',
    totalAssets: undefined,
  },
  liabilities: {
    creditCardDebt: undefined,
    carLoans: undefined,
    studentLoans: undefined,
    otherDebts: undefined,
    alimonyChildSupport: undefined,
    totalMonthlyDebt: undefined,
  },
  property: {
    address: '',
    city: '',
    state: '',
    zip: '',
    county: '',
    propertyValue: undefined,
    propertyType: '',
    occupancyType: '',
    unitsCount: 1,
    mixedUse: false,
    manufacturedHome: false,
  },
  loanDetails: {
    loanPurpose: '',
    loanAmount: undefined,
    downPayment: undefined,
    loanType: '',
    interestRate: undefined,
    loanTerm: undefined,
    currentMortgageBalance: undefined,
    currentInterestRate: undefined,
    cashOutAmount: undefined,
    refinancePurpose: '',
  },
  declarations: {
    outstandingJudgments: false,
    bankruptcyLast7Years: false,
    foreclosureLast7Years: false,
    lawsuitParty: false,
    delinquentFederalDebt: false,
    alimonyObligation: false,
    downPaymentBorrowed: false,
    usCitizen: true,
    permanentResident: false,
    currentlyServing: false,
    previouslyServed: false,
  },
};

/** Apply zod issues onto RHF via setError. */
export function applyZodErrors(result, setError, rootKey) {
  if (result.success) return;
  for (const issue of result.error.issues) {
    const path = issue.path.length ? issue.path.join('.') : rootKey;
    setError(path, { type: 'manual', message: issue.message });
  }
}
